import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import type React from 'react'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import {
  aboveNotificationBar,
  type NotificationBarOffsetProps,
  useNotificationBarOffsetProps,
} from 'core/components/notification-bar/NotificationBarOffsetContext'
import BadgeWithIcon, { type BadgeVariant } from 'core/elements/badge/BadgeWithIcon'
import { ACTION_BAR_HEIGHT } from 'core/elements/grid/gridConstants'
import Text from 'core/elements/Text'
import type Theme from 'core/themes/model'

// FR-ID: TT-FR-001..006, spec: src/app/core/components/task-tray/TaskTray.spec.md
export type TaskTrayStatus = 'inProgress' | 'success' | 'attention' | 'failed'

export interface TaskTrayItem {
  id: string
  status: TaskTrayStatus
  message: React.ReactNode
  actionLabel?: string
  // Accessible name of the card, e.g. what the task is about
  label?: string
}

export interface TaskTrayProps {
  title: string
  items: TaskTrayItem[]
  onAction?: (item: TaskTrayItem) => void
  onDismiss?: () => void
}

// Worst status wins the header chip when nothing is running: failed > attention > success.
const statusPriority: Record<Exclude<TaskTrayStatus, 'inProgress'>, number> = {
  failed: 3,
  attention: 2,
  success: 1,
}

const chipConfig: Record<
  TaskTrayStatus,
  { label: string; icon: string; variant: BadgeVariant; spin?: boolean }
> = {
  inProgress: { label: 'In progress', icon: 'arrows-rotate', variant: 'primary', spin: true },
  success: { label: 'Completed', icon: 'check', variant: 'success' },
  attention: { label: 'Needs attention', icon: 'triangle-exclamation', variant: 'warning' },
  failed: { label: 'Failed', icon: 'xmark', variant: 'error' },
}

// Picks the chip status: in progress wins outright, otherwise the worst ended status.
const getTrayStatus = (items: TaskTrayItem[]): TaskTrayStatus => {
  if (items.some((item) => item.status === 'inProgress')) return 'inProgress'
  return items.reduce<Exclude<TaskTrayStatus, 'inProgress'>>((worst, item) => {
    const status = item.status as Exclude<TaskTrayStatus, 'inProgress'>
    return statusPriority[status] > statusPriority[worst] ? status : worst
  }, 'success')
}

// Maps item status to the badge swatch key used for the bottom bar color.
const barClassByStatus: Record<TaskTrayStatus, string> = {
  inProgress: 'cardBarInProgress',
  success: 'cardBarSuccess',
  attention: 'cardBarAttention',
  failed: 'cardBarFailed',
}

export default function TaskTray({ title, items, onAction, onDismiss }: TaskTrayProps) {
  const classes = useStyles(useNotificationBarOffsetProps())
  const [collapsed, setCollapsed] = useState(false)

  const trayStatus = useMemo(() => getTrayStatus(items), [items])

  if (items.length === 0) return null

  const chip = chipConfig[trayStatus]

  return createPortal(
    <section className={classes.tray} aria-label={title}>
      <div className={classes.header}>
        <Text variant="body1" className={classes.title}>
          {title}
        </Text>
        <div className={classes.headerActions}>
          <div aria-live="polite">
            <BadgeWithIcon
              text={chip.label}
              icon={chip.icon}
              variant={chip.variant}
              spin={chip.spin}
              hideTooltip
            />
          </div>
          <button
            type="button"
            className={classes.iconButton}
            aria-label={collapsed ? 'Expand task tray' : 'Collapse task tray'}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed(!collapsed)}
          >
            <FontAwesomeIcon size="md" solid>
              {collapsed ? 'chevron-down' : 'chevron-up'}
            </FontAwesomeIcon>
          </button>
          <button
            type="button"
            className={classes.iconButton}
            aria-label="Dismiss task tray"
            onClick={() => onDismiss?.()}
          >
            <FontAwesomeIcon size="md" solid>
              xmark
            </FontAwesomeIcon>
          </button>
        </div>
      </div>
      {!collapsed && (
        <ul className={classes.body}>
          {items.map((item) => (
            <li key={item.id} className={classes.card} aria-label={item.label}>
              <div className={classes.cardContent}>
                <Text variant="body2" className={classes.message}>
                  {item.message}
                </Text>
                {item.actionLabel && (
                  <button
                    type="button"
                    className={classes.actionLink}
                    aria-label={item.actionLabel}
                    onClick={() => onAction?.(item)}
                  >
                    {item.actionLabel}
                  </button>
                )}
              </div>
              <div className={clsx(classes.cardBar, classes[barClassByStatus[item.status]])} />
            </li>
          ))}
        </ul>
      )}
    </section>,
    document.body,
  )
}

const useStyles = makeStyles<Theme, NotificationBarOffsetProps>((theme) => ({
  // Frame and header match the Support Bundle popup (SupportBundleProgressPopover).
  tray: {
    position: 'fixed',
    right: theme.spacing(3),
    // NB-FR-001 (NotificationBar.spec.md): above the admin notification bar and its drawer
    bottom: aboveNotificationBar(theme.spacing(3)),
    width: '100%',
    maxWidth: 480,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    // Same as the Support Bundle popup: just under the app's drawers and modals (Modal uses
    // 1000), so it never covers their buttons.
    zIndex: 990,
    overflow: 'hidden',
    // While rows are selected, the grid's floating batch action bar sits at the same bottom
    // offset; lift the tray above it so it never covers the bar's buttons on narrow screens.
    'body:has(#floating-action-portal-root > *) &': {
      bottom: aboveNotificationBar(theme.spacing(3) + ACTION_BAR_HEIGHT + theme.spacing(2)),
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[100],
  },
  title: {
    fontWeight: 600,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(0.5),
  },
  body: {
    // <ul> reset: no bullets or default margin (padding is set below)
    listStyle: 'none',
    margin: 0,
    maxHeight: '50vh',
    overflowY: 'auto',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  card: {
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 6,
    overflow: 'hidden',
  },
  cardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
  message: {
    wordBreak: 'break-word',
    flex: 1,
  },
  actionLink: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontWeight: 700,
    color: theme.components.badge.primary.color,
    whiteSpace: 'nowrap',
  },
  cardBar: {
    height: 4,
  },
  cardBarInProgress: {
    backgroundColor: theme.components.badge.primary.color,
  },
  cardBarSuccess: {
    backgroundColor: theme.components.badge.success.color,
  },
  cardBarAttention: {
    backgroundColor: theme.components.badge.warning.color,
  },
  cardBarFailed: {
    backgroundColor: theme.components.badge.error.color,
  },
}))
